import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, ImagePlaceholder } from "@/components/site/SiteShell";
import { PageHero, Section, CTA, Breadcrumbs, Prose } from "@/components/site/Sections";

const TITLE = "TEKNIQ Garanti | Elektriker-Elektriker København";
const DESCRIPTION =
  "Som medlem af TEKNIQ er Elektriker-Elektriker omfattet af TEKNIQ Garanti. Dækning op til 150.000 kr. ved fejl og mangler – gratis for dig som forbruger.";

export const Route = createFileRoute("/tekniq-garanti")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/tekniq-garanti" },
    ],
    links: [{ rel: "canonical", href: "https://elektriker-elektriker.dk/tekniq-garanti" }],
  }),
  component: TekniqGarantiPage,
});

function TekniqGarantiPage() {
  return (
    <SiteShell>
      <Breadcrumbs items={[{ label: "Forside", to: "/" }, { label: "TEKNIQ Garanti" }]} />
      <PageHero
        variant="centered"
        eyebrow="Tryghed for dig som kunde"
        title="Medlem af TEKNIQ Garanti"
        intro="Som medlem af TEKNIQ er Elektriker-Elektriker omfattet af TEKNIQ Garanti. Det betyder, at du som forbruger har et ekstra sikkerhedsnet, hvis der skulle opstå fejl eller mangler ved vores arbejde – helt gratis for dig."
        imageLabel="TEKNIQ Garanti logo"
      />

      <Section>
        <Prose>
          <h2>Hvad er TEKNIQ Garanti?</h2>
          <p>
            TEKNIQ Garanti fungerer som et sikkerhedsnet under forbrugere ved fejl og mangler
            og uenigheder om prisen. Garantien er gratis for forbrugere.
          </p>
          <p>
            Får en forbruger medhold i Ankenævnet for Tekniske Installationer, og virksomheden
            ikke opfylder kendelsen, kan TEKNIQ Garanti træde til. Ordningen dækker op til
            150.000 kr.
          </p>

          <h2>Hvornår dækker garantien?</h2>
          <p>
            Garantiordningen dækker, når virksomheden er medlem af TEKNIQ på det tidspunkt,
            hvor Ankenævnet for Tekniske Installationer modtager klagen.
          </p>

          <h2>Hvad er ikke dækket?</h2>
          <p>
            Garantiordningen dækker ikke i sager, hvor der klages over elinstallationsrapporter.
            Garantiordningen dækker heller ikke arbejde udført for foreninger, andre
            erhvervsdrivende mv.
          </p>

          <h2>Fordele for dig som kunde</h2>
          <p>
            TEKNIQ Garanti skaber tryghed hos forbrugerne – men den er også en konkurrencefordel
            for virksomheden. Ordningen styrker branchens omdømme, skaber tillid til
            virksomheden og er dermed med til at skaffe virksomheden flere kunder.
          </p>
          <p>
            Når du vælger Elektriker-Elektriker, får du altså ikke bare autoriseret håndværk
            og garanti på arbejdet. Du får også den ekstra tryghed, som TEKNIQ Garanti
            giver – en ordning, der er unik for branchen og som kun medlemsvirksomheder kan tilbyde.
          </p>
        </Prose>
      </Section>

      <Section className="!pt-0">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          <ImagePlaceholder label="TEKNIQ certificering" ratio="aspect-[5/4]" />
          <div>
            <h2 className="text-3xl">Din sikkerhed i tal</h2>
            <ul className="mt-5 space-y-3 text-[var(--ink)]/85">
              {[
                "Dækning op til 150.000 kr. ved fejl og mangler",
                "Gratis for dig som forbruger",
                "Gælder ved medhold i Ankenævnet for Tekniske Installationer",
                "Kun autoriserede TEKNIQ-medlemsvirksomheder kan tilbyde ordningen",
                "Supplerer vores egen garanti på arbejdet",
              ].map((b) => (
                <li key={b} className="flex gap-3">
                  <span aria-hidden className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l4 4 10-10" /></svg>
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex gap-3">
              <Link
                to="/tilbud"
                className="inline-flex h-11 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:brightness-95"
              >
                Indhent tilbud
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex h-11 items-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="rounded-2xl border border-border bg-secondary/40 p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl">Hvad er Ankenævnet for Tekniske Installationer?</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Ankenævnet for Tekniske Installationer er et uafhængigt ankenævn, der behandler klager
            over el-, vvs- og kloakinstallationer. Nævnet kan træffe afgørelse i uenigheder mellem
            forbrugere og installatører – både om fejl og mangler og om prisen på arbejdet.
            Afgørelserne er bindende for virksomheden, og det er her TEKNIQ Garanti træder til,
            hvis virksomheden ikke lever op kendelsen.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Du kan læse mere om Ankenævnet og klageproceduren på{" "}
            <a
              href="https://www.tekniq.dk"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[var(--ink)] underline underline-offset-4 hover:text-primary"
            >
              tekniq.dk
            </a>
            .
          </p>
        </div>
      </Section>

      <CTA />
      <div className="h-10" />
    </SiteShell>
  );
}
